import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PipelineView from './PipelineView';
import InvoicesView from './InvoicesView';
import NewDealModal from './NewDealModal';
import ViewEditDealModal from './ViewEditDealModal';
import RefundModal from './RefundModal';
import DealsTableWithShared from './DealsTableWithShared';
import DealsImportModal from './DealsImportModal';
import { hasPermission, canModifyDeal, getDealsViewAccess } from '../../utils/permissions';
import { DeleteConfirmationModal, useNotification, TabNavigation } from '../common';
import Button from '../common/Button';
import useModuleSettings from '../../hooks/useModuleSettings';
import ContractsList from '../contracts/ContractsList';

const DealsList = () => {
  // Get notification functions from context
  const { showNotification } = useNotification();
  const { moduleSettings } = useModuleSettings();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('dealsActiveTab') || 'deals'; // 'deals', 'invoices', or 'contracts'
  });
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dealsViewMode') || 'pipeline'; // 'list' or 'pipeline'
  });
  const canCreate = hasPermission('deals', 'create');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewEditModalOpen, setIsViewEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('dealsSearchTerm') || '';
  });
  // Initialize filters from localStorage or use defaults
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('dealsFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      country: '',
      course: '',
      deal_owner_id: '',
      created_after: '',
      created_before: '',
      created_date_option: '',
      paid_after: '',
      paid_before: '',
      paid_date_option: ''
    };
  });
  
  // Initialize active filters from localStorage or use empty object
  const [activeFilters, setActiveFilters] = useState(() => {
    const savedActiveFilters = localStorage.getItem('dealsActiveFilters');
    return savedActiveFilters ? JSON.parse(savedActiveFilters) : {};
  });
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  // Initialize limit from localStorage or default to 20
  const initialLimit = parseInt(localStorage.getItem('dealsTableLimit') || '20', 10);
  
  // Bulk selection state
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkActionData, setBulkActionData] = useState({
    stage_name: '',
    deal_owner_id: '',
    country: ''
  });
  
  // State for pipeline stages in bulk edit
  const [bulkEditStages, setBulkEditStages] = useState([]);
  
  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundDealId, setRefundDealId] = useState(null);
  const [refundStageId, setRefundStageId] = useState(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Search timeout ref
  const searchTimeout = useRef(null);
  
  // Add paid_date to sortable columns without changing default sort behavior
  
  // Column management
  const [showColumnModal, setShowColumnModal] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: [20, 50, 100].includes(initialLimit) ? initialLimit : 20, // Ensure valid limit
    total: 0,
    pages: 0
  });
  
  // Default columns for reference in sorting
  const defaultColumns = [
    { id: 'title', label: 'Title', visible: true, width: 160 },
    { id: 'lead_name', label: 'Customer', visible: true, width: 160 },
    { id: 'course', label: 'Course', visible: true, width: 150 },
    { id: 'country', label: 'Country', visible: true, width: 100 },
    { id: 'value', label: 'Value', visible: true, width: 120, sortable: true },
    { id: 'value_companycurrency', label: 'Value in Company Currency', visible: true, width: 160, sortable: true, sortField: 'value_companycurrency' },
    { id: 'payment_method', label: 'Payment', visible: true, width: 100 },
    { id: 'stage_name', label: 'Stage', visible: true, width: 140 },
    { id: 'original_source', label: 'Original Source', visible: true, width: 130 },
    { id: 'latest_source', label: 'Latest Source', visible: true, width: 130 },
    { id: 'lesson_progress', label: 'Lessons', visible: true, width: 120 },
    { id: 'owner_name', label: 'Deal Owner', visible: true, width: 130 },
    { id: 'created_at', label: 'Created', visible: true, sortable: true, width: 100 },
    { id: 'paid_date', label: 'Paid Date', visible: true, sortable: true, width: 100 }
  ];

  // Fetch deals data with filters
  // Modified to accept an optional limit override
  const fetchDeals = async (page = pagination.page, limitOverride = null) => {
    const currentLimit = limitOverride !== null ? limitOverride : pagination.limit;
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Build query parameters from active filters
      const params = {
        ...activeFilters,
        page: page,
        limit: currentLimit, // Use currentLimit here
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      // Add search term if it exists
      if (searchTerm && searchTerm.trim() !== '') {
        const trimmedSearch = searchTerm.trim();
        // If it's an email, add it as a special parameter
        if (isEmailAddress(trimmedSearch)) {
          console.log('Searching for email:', trimmedSearch);
          params.search = trimmedSearch;
          params.exact_email = 'true'; // Tell the backend to do an exact match
        } else {
          console.log('Searching for text:', trimmedSearch);
          params.search = trimmedSearch;
        }
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        },
        params: params
      };
      
      console.log('Request params:', config.params);
      const response = await axios.get('/api/deals', config);
      
      // Log the response for debugging
      console.log('Search response:', {
        total: response.data.pagination?.total || 'unknown',
        deals: response.data.deals?.length || 0,
        sampleDeal: response.data.deals && response.data.deals.length > 0 ? response.data.deals[0] : null
      });
      
      // Check if response contains pagination info
      if (response.data.pagination) {
        setDeals(response.data.deals || []);
        setPagination(response.data.pagination);
      } else {
        // Fallback for backward compatibility
        setDeals(response.data);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching deals:', err);
      setError('Failed to load deals');
      setLoading(false);
    }
  };
  
  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    // Fetch deals for the new page using the current limit
    fetchDeals(newPage);
  };
  
  // Handle limit change for pagination
  const handleLimitChange = (newLimit) => {
    const limitValue = parseInt(newLimit, 10);
    if (![20, 50, 100].includes(limitValue)) return; // Ignore invalid values

    console.log(`Changing limit to ${limitValue}`);
    // Save the new limit to localStorage
    localStorage.setItem('dealsTableLimit', limitValue.toString());
    // Update pagination state first, resetting page to 1
    setPagination(prev => ({ ...prev, limit: limitValue, page: 1 }));
    // Explicitly fetch deals with page 1 and the new limit value
    fetchDeals(1, limitValue);
  };
  
  // Handle sorting
  const handleSort = (columnId) => {
    // Get the actual field to sort by if the column has a sortField property
    const visibleColumns = JSON.parse(localStorage.getItem('dealsTableColumns')) || defaultColumns;
    const column = visibleColumns.find(col => col.id === columnId);
    const fieldToSortBy = column?.sortField || columnId;
    
    setSortOrder(prevSortOrder => {
      if (sortBy === fieldToSortBy) {
        return prevSortOrder === 'asc' ? 'desc' : 'asc';
      }
      return 'asc'; // Default to ascending for a new column
    });
    
    setSortBy(fieldToSortBy);
  };
  
  // Fetch users for filters
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Use same API endpoint with has_deals_access filter parameter as NewDealModal.js
      const response = await axios.get('/api/auth/users?has_deals_access=true', config);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  
  // Fetch courses for filters
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/settings/courses/active', config);
      setCourses(response.data);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for course filter
    if (name === 'course' && value) {
      // Split the combined value and just use the course name for filtering
      const courseParts = value.split('|');
      if (courseParts.length > 1) {
        setFilters(prev => ({
          ...prev,
          [name]: courseParts[1] // Use course name for backward compatibility with backend
        }));
        return;
      }
    }
    
    // Special handling for date inputs
    if ((name === 'created_after' || name === 'created_before' || name === 'paid_after' || name === 'paid_before') && 
        e.target.type === 'date') {
      // Convert the date input value to ISO string for API compatibility
      if (value) {
        // Store the date value directly to avoid timezone issues
        // For the input display, we'll use the raw date string
        // For API filtering, we'll append the appropriate time based on field type
        
        // Store this format to avoid any date shifting/timezone issues
        setFilters(prev => ({
          ...prev,
          [name]: value + (name.endsWith('before') ? 'T23:59:59.999Z' : 'T00:00:00.000Z')
        }));
      } else {
        // Handle empty date values explicitly
        setFilters(prev => ({
          ...prev,
          [name]: ''
        }));
      }
      return;
    }
    
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Apply filters with localStorage persistence
  const applyFilters = () => {
    // Debug: Log the current filters state
    console.log('Applying filters. Current filters state:', filters);
    
    // Only include filters with values
    const newActiveFilters = Object.entries(filters)
      .filter(([_, value]) => value !== '')
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    
    console.log('Active filters after filtering:', newActiveFilters);
    
    // Save to localStorage
    localStorage.setItem('dealsActiveFilters', JSON.stringify(newActiveFilters));
    localStorage.setItem('dealsFilters', JSON.stringify(filters));
    
    setActiveFilters(newActiveFilters);
    
    // Reset pagination when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Close filters panel on mobile
    if (window.innerWidth < 768) {
      setShowFilters(false);
    }
  };
  
  // Clear filters and remove from localStorage
  const clearFilters = () => {
    const emptyFilters = {
      country: '',
      course: '',
      deal_owner_id: '',
      created_after: '',
      created_before: '',
      created_date_option: '',
      paid_after: '',
      paid_before: '',
      paid_date_option: ''
    };
    
    // Clear localStorage
    localStorage.removeItem('dealsActiveFilters');
    localStorage.setItem('dealsFilters', JSON.stringify(emptyFilters));
    
    setFilters(emptyFilters);
    setActiveFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;
  
  // Generate date options for filtering
  const getDateRangeOptions = () => {
    const today = new Date();
    const startOfToday = new Date(today.setHours(0, 0, 0, 0)).toISOString();
    
    // This week (Sunday to Saturday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Last week
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfWeek);
    endOfLastWeek.setMilliseconds(-1);
    
    // This month
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Last month
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
    
    return [
      { label: 'Today', value: `${startOfToday}` },
      { label: 'This week', value: `${startOfWeek.toISOString()}` },
      { label: 'Last week', value: `${startOfLastWeek.toISOString()},${endOfLastWeek.toISOString()}` },
      { label: 'This month', value: `${startOfMonth.toISOString()}` },
      { label: 'Last month', value: `${startOfLastMonth.toISOString()},${endOfLastMonth.toISOString()}` }
    ];
  };
  
  // Check if a string is likely an email address
  const isEmailAddress = (text) => {
    return /\S+@\S+\.\S+/.test(text);
  };
  
  // Function to trigger search with current search term
  const triggerSearch = (term) => {
    // Reset pagination to first page
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Build query parameters
    const params = {
      ...activeFilters,
      page: 1,
      limit: pagination.limit,
      sort_by: sortBy,
      sort_order: sortOrder
    };
    
    // Add search term if it exists
    if (term && term.trim() !== '') {
      const trimmedSearch = term.trim();
      if (isEmailAddress(trimmedSearch)) {
        console.log('Searching for email:', trimmedSearch);
        params.search = trimmedSearch;
        params.exact_email = 'true';
      } else {
        params.search = trimmedSearch;
      }
    }
    
    // Execute search
    console.log('Executing search with params:', params);
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setLoading(true);
    
    axios.get('/api/deals', {
      headers: {
        'x-auth-token': token
      },
      params: params
    })
    .then(response => {
      console.log('Search results:', {
        total: response.data.pagination?.total || 'unknown',
        deals: response.data.deals?.length || 0
      });
      
      if (response.data.pagination) {
        setDeals(response.data.deals || []);
        setPagination(response.data.pagination);
      } else {
        setDeals(response.data);
      }
      
      setLoading(false);
    })
    .catch(err => {
      console.error('Error fetching deals:', err);
      setError('Failed to load deals');
      setLoading(false);
    });
  };
  
  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Save search term to localStorage
    localStorage.setItem('dealsSearchTerm', value);
    
    // Clear any existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Set a new timeout for debouncing (300ms)
    searchTimeout.current = setTimeout(() => {
      triggerSearch(value);
    }, 300);
  };
  
  // Fetch deals when activeFilters, sorting, or pagination page changes
  useEffect(() => {
    if (activeTab === 'deals' && viewMode === 'list') {
      fetchDeals();
    }
    // Removed pagination.limit dependency, handled by handleLimitChange
    // Note: searchTerm is deliberately not in the dependency array because it's handled by the debounced handler
  }, [activeTab, viewMode, activeFilters, sortBy, sortOrder, pagination.page]); 
  
  // Fetch users, courses, and pipeline stages on initial load
  useEffect(() => {
    if (activeTab === 'deals') {
      fetchUsers();
      fetchCourses();
      
      // Fetch pipeline stages for bulk edit
      const fetchBulkEditStages = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          
          const response = await axios.get('/api/deals/stages/all', {
            headers: { 'x-auth-token': token }
          });
          setBulkEditStages(response.data);
        } catch (err) {
          console.error('Error fetching pipeline stages:', err);
        }
      };
      
      fetchBulkEditStages();
      
      // Trigger search with the loaded search term (if any)
      if (searchTerm) {
        triggerSearch(searchTerm);
      }
    }
  }, [activeTab, searchTerm]);
  
  // Sync filters with activeFilters on component mount
  useEffect(() => {
    if (Object.keys(activeFilters).length > 0) {
      setFilters(prevFilters => ({
        ...prevFilters,
        ...activeFilters
      }));
    }
  }, []); // Only run on mount
  
  const handleDealCreated = (newDeal) => {
    setDeals(prevDeals => [newDeal, ...prevDeals]);
    
    // Dispatch a custom event to notify other components (like PipelineView) that a deal was created
    const dealCreatedEvent = new CustomEvent('dealCreated', { detail: newDeal });
    window.dispatchEvent(dealCreatedEvent);
    
    // Show success notification
    showNotification(`Deal "${newDeal.title}" created successfully`, 'success');
  };
  
  const handleDealUpdated = (updatedDeal) => {
    setDeals(prevDeals => 
      prevDeals.map(deal => {
        if (deal.id === updatedDeal.id) {
          // Preserve lead_name if it's not present in the updated deal
          if (!updatedDeal.lead_name && deal.lead_name) {
            return { ...updatedDeal, lead_name: deal.lead_name };
          }
          return updatedDeal;
        }
        return deal;
      })
    );
  };
  
  const handleViewDeal = (dealId) => {
    setSelectedDealId(dealId);
    setIsViewEditModalOpen(true);
  };
  
  const handleDeleteDeal = async (dealId) => {
    // Get the deal to delete from state
    const dealToDelete = deals.find(deal => deal.id === dealId);
    if (!dealToDelete) return;
    
    // Check if user has permission to delete this deal
    if (!canModifyDeal('delete', dealToDelete)) {
      showNotification('You do not have permission to delete this deal.', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete('/api/deals/' + dealId, config);
      
      // Remove the deleted deal from state
      const updatedDeals = deals.filter(deal => deal.id !== dealId);
      setDeals(updatedDeals);
      
      // Update pagination total count
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1,
        pages: Math.ceil((prev.total - 1) / prev.limit)
      }));
      
      // Show success notification
      showNotification('Deal deleted successfully');
      
    } catch (err) {
      console.error('Error deleting deal:', err);
      
      let errorMessage = 'Failed to delete deal';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        errorMessage += `: ${err.response.data.message || err.response.statusText}`;
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage += ': No response from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage += `: ${err.message}`;
      }
      
      showNotification(errorMessage, 'error');
    }
  };
  
  // Update deal stage directly from the list view
  const updateDealStage = async (dealId, newStageName) => {
    try {
      // Get the deal to update
      const dealToUpdate = deals.find(deal => deal.id === dealId);
      if (!dealToUpdate) return;
      
      // Check if user has permission to update this deal
      if (!canModifyDeal('update', dealToUpdate)) {
        alert('You do not have permission to update this deal.');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      console.log("Updating deal stage to:", newStageName);
      
      // Find the stage_id for the given stage name
      const stageResult = await axios.get(
        '/api/deals/stages/all',
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      const stages = stageResult.data;
      const stageId = stages.find(stage => stage.name === newStageName)?.id;
      
      if (!stageId) {
        console.error("Could not find stage_id for stage name:", newStageName);
        alert('Failed to update deal stage: Could not find stage ID');
        return;
      }
      
      console.log("Found stage_id for", newStageName, ":", stageId);
      
      // Special handling for Refunded stage - show refund modal
      if (newStageName === 'Refunded') {
        setRefundDealId(dealId);
        setRefundStageId(stageId);
        setShowRefundModal(true);
        return; // Exit early, will continue after refund amount is entered
      }
      
      // Use PATCH endpoint which is specifically for updating the stage
      const res = await axios.patch(
        '/api/deals/' + dealId + '/stage',
        {
          stage_id: stageId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      console.log("Stage update response:", res.data);
      
      // Update the deal in the deals list, preserving lead_name if missing
      const updatedDeals = deals.map(deal => {
        if (deal.id === dealId) {
          // Preserve lead_name and any other data that might be missing
          return { 
            ...deal, 
            ...res.data,
            stage_name: newStageName,
            lead_name: deal.lead_name || res.data.lead_name
          };
        }
        return deal;
      });
      
      setDeals(updatedDeals);
      
    } catch (err) {
      console.error('Error updating deal stage:', err);
      console.error('Error details:', err.response?.data || err.message);
      alert('Failed to update deal stage. Please try again.');
    }
  };
  
  // Handle refund confirmation
  const handleRefundConfirm = async (refundAmount) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      const res = await axios.patch(
        '/api/deals/' + refundDealId + '/stage',
        {
          stage_id: refundStageId,
          refunded_amount: refundAmount
        },
        config
      );
      
      // Update the deal in the deals list
      const updatedDeals = deals.map(deal => {
        if (deal.id === refundDealId) {
          // Get the stage name
          const stageName = 'Refunded';
          
          // Preserve lead_name and any other data that might be missing
          return { 
            ...deal, 
            ...res.data,
            stage_name: stageName,
            refunded_amount: refundAmount,
            lead_name: deal.lead_name || res.data.lead_name
          };
        }
        return deal;
      });
      
      setDeals(updatedDeals);
      
      // Close modal and show success message
      setShowRefundModal(false);
      alert(`Refund of ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: deals.find(d => d.id === refundDealId)?.currency || 'USD',
      }).format(refundAmount)} recorded successfully!`);
      
    } catch (err) {
      console.error('Error updating deal stage with refund:', err);
      alert('Failed to update deal stage. Please try again.');
      setShowRefundModal(false);
    }
    
    // Reset refund state
    setRefundDealId(null);
    setRefundStageId(null);
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    
    const formatOptions = {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    };
    
    return new Intl.NumberFormat('en-US', formatOptions).format(amount);
  };
  
  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDeals([]);
      setSelectAll(false);
    } else {
      setSelectedDeals(deals.map(deal => deal.id));
      setSelectAll(true);
    }
  };
  
  // Execute bulk action
  const executeBulkAction = async () => {
    if (selectedDeals.length === 0) return;
    
    // Check permissions for each selected deal
    const dealsToProcess = [];
    const unauthorizedDeals = [];
    
    for (const dealId of selectedDeals) {
      const deal = deals.find(d => d.id === dealId);
      if (deal) {
        if (canModifyDeal(bulkAction === 'delete' ? 'delete' : 'update', deal)) {
          dealsToProcess.push(dealId);
        } else {
          unauthorizedDeals.push(deal.title || `Deal #${deal.id}`);
        }
      }
    }
    
    if (unauthorizedDeals.length > 0) {
      alert(`You don't have permission to ${bulkAction === 'delete' ? 'delete' : 'modify'} the following deals: ${unauthorizedDeals.join(', ')}`);
      if (dealsToProcess.length === 0) return;
    }
    
    const token = localStorage.getItem('token');
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    };
    
    try {
      let updatedDeals = [...deals];
      
      if (bulkAction === 'delete') {
        // Show confirmation modal
        setShowDeleteConfirm(true);
        return; // Exit here, the actual deletion will be handled when modal is confirmed
        
        // The actual deletion logic is now in executeBulkDelete function
      } else if (bulkAction === 'update') {
        // Update selected deals
        const updateData = {};
        
        // Only include fields with values
        if (bulkActionData.stage_name) updateData.stage_name = bulkActionData.stage_name;
        if (bulkActionData.deal_owner_id) {
          // Handle special case for unassigning (value is "null" string)
          if (bulkActionData.deal_owner_id === "null") {
            updateData.deal_owner_id = null;
          } else {
            updateData.deal_owner_id = bulkActionData.deal_owner_id;
          }
        }
        if (bulkActionData.course_id) updateData.course_id = bulkActionData.course_id;
        if (bulkActionData.country) updateData.country = bulkActionData.country;
        
        // Update deals one by one and update local state
        for (const dealId of dealsToProcess) {
          const dealToUpdate = deals.find(deal => deal.id === dealId);
          if (dealToUpdate) {
            const updatedDeal = { ...dealToUpdate, ...updateData };
            const res = await axios.put('/api/deals/' + dealId, updatedDeal, config);
            
            // Update in local state
            updatedDeals = updatedDeals.map(deal => deal.id === dealId ? res.data : deal);
          }
        }
      }
      
      // Update state with changes
      setDeals(updatedDeals);
      setSelectedDeals([]);
      setSelectAll(false);
      setShowBulkActionModal(false);
      setBulkAction('');
    } catch (err) {
      console.error('Error executing bulk action:', err);
      alert('An error occurred while executing the bulk action. Please try again.');
    }
  };
  
  // Reset selections when deals change
  useEffect(() => {
    setSelectedDeals([]);
    setSelectAll(false);
  }, [deals]);
  
  // Execute delete when confirmation modal is confirmed (handles both single and bulk deletion)
  
  const executeBulkDelete = async () => {
    try {
      // If it's a single deal deletion
      if (selectedDeals.length === 1) {
        await handleDeleteDeal(selectedDeals[0]);
        setSelectedDeals([]);
        return;
      }
      
      // If it's bulk deletion
      const dealsToProcess = selectedDeals.filter(dealId => {
        const deal = deals.find(d => d.id === dealId);
        return deal && canModifyDeal('delete', deal);
      });
      
      if (dealsToProcess.length === 0) return;
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      let updatedDeals = [...deals];
      
      // Delete deals one by one
      for (const dealId of dealsToProcess) {
        try {
          await axios.delete('/api/deals/' + dealId, config);
          // Remove from local state
          updatedDeals = updatedDeals.filter(deal => deal.id !== dealId);
        } catch (deleteError) {
          console.error(`Error deleting deal ${dealId}:`, deleteError);
          // Continue with next deal even if one fails
          
          // If the deal was already deleted (404), we should still remove it from our local state
          if (deleteError.response && deleteError.response.status === 404) {
            updatedDeals = updatedDeals.filter(deal => deal.id !== dealId);
          }
        }
      }
      
      // Update state with changes
      setDeals(updatedDeals);
      
      // Update pagination total count
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - dealsToProcess.length),
        pages: Math.max(1, Math.ceil((prev.total - dealsToProcess.length) / prev.limit))
      }));
      
      setSelectedDeals([]);
      setSelectAll(false);
      setShowBulkActionModal(false);
      setBulkAction('');
      
      // Show success notification
      showNotification(`Successfully deleted ${dealsToProcess.length} deals`);
      
    } catch (err) {
      console.error('Error deleting deals:', err);
      showNotification('An error occurred while deleting deals. Please try again.', 'error');
    }
  };
  
  // Create tabs array dynamically based on enabled modules
  const tabs = [
    {
      id: 'deals',
      label: 'Deals',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
      )
    }
  ];

  // Add contracts tab if module is enabled
  if (moduleSettings.contracts) {
    tabs.push({
      id: 'contracts',
      label: 'Contracts',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM8 8a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm1 3a1 1 0 100 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
        </svg>
      )
    });
  }

  return (
    <div>
      {/* Using shared TabNavigation component */}
      <TabNavigation 
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          localStorage.setItem('dealsActiveTab', tab);
        }}
      />

      {activeTab === 'deals' ? (
        <>
          {/* View Toggle, Filters and Add Deal Button */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1">
              <div className="flex space-x-1">
                <Button 
                  onClick={() => {
                    setViewMode('list');
                    localStorage.setItem('dealsViewMode', 'list');
                  }}
                  variant={viewMode === 'list' ? 'primary' : 'outline'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  List View
                </Button>
                <Button 
                  onClick={() => {
                    setViewMode('pipeline');
                    localStorage.setItem('dealsViewMode', 'pipeline');
                  }}
                  variant={viewMode === 'pipeline' ? 'primary' : 'outline'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Pipeline View
                </Button>
              </div>
              
              {/* Action Buttons Removed - Now using only the right-aligned button */}
              
              {/* Search Input */}
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onPaste={(e) => {
                    // Prevent default to avoid duplicate content
                    e.preventDefault();
                    
                    // Get current cursor position and current value
                    const cursorPos = e.target.selectionStart;
                    const currentValue = searchTerm;
                    
                    // Get pasted text
                    const pastedText = e.clipboardData.getData('text');
                    
                    // Create new value with pasted text inserted at cursor position
                    const newValue = currentValue.substring(0, cursorPos) + 
                                    pastedText + 
                                    currentValue.substring(e.target.selectionEnd);
                    
                    // Update search term
                    setSearchTerm(newValue);
                    
                    // Clear any existing timeout
                    if (searchTimeout.current) {
                      clearTimeout(searchTimeout.current);
                    }
                    
                    // Directly trigger search with the new value
                    triggerSearch(newValue);
                  }}
                  placeholder="Search deals by title or customer..."
                  className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      // Remove search term from localStorage
                      localStorage.removeItem('dealsSearchTerm');
                      // Clear search with empty term
                      triggerSearch('');
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={activeFilterCount > 0 ? 'secondary' : 'outline'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
              </Button>
              
              {viewMode === 'list' && (
                <Button
                  onClick={() => setShowColumnModal(true)}
                  variant="outline"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                  Columns
                </Button>
              )}
              
              {/* Import/Export dropdown */}
              <div className="relative inline-block text-left">
                <div className="group">
                  <Button
                    variant="outline"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3l-3-3" />
                    </svg>
                    Import/Export
                    <svg className="-mr-1 ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Button>
                  <div className="hidden group-hover:block absolute z-50 mt-0 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      {hasPermission('deals', 'create') && (
                        <Button
                          onClick={() => setIsImportModalOpen(true)}
                          variant="ghost"
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 bg-transparent hover:bg-gray-100 rounded-none shadow-none ring-0 border-0 justify-start"
                          role="menuitem"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Import CSV
                        </Button>
                      )}
                      
                      {hasPermission('deals', 'export') && (
                        <Button
                          onClick={() => {
                            // Export deals to CSV
                            const token = localStorage.getItem('token');
                            
                            axios({
                              url: '/api/deals/export/csv',
                              method: 'GET',
                              responseType: 'blob',
                              headers: {
                                'x-auth-token': token
                              },
                              params: activeFilters 
                            })
                            .then(response => {
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              
                              const contentDisposition = response.headers['content-disposition'];
                              let filename = 'deals-export.csv';
                              if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                                if (filenameMatch && filenameMatch[1]) {
                                  filename = filenameMatch[1];
                                }
                              }
                              
                              link.setAttribute('download', filename);
                              document.body.appendChild(link);
                              link.click();
                              
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(link);
                            })
                            .catch(error => {
                              console.error('Error exporting CSV:', error);
                              showNotification('Failed to export deals. Please try again.', 'error');
                            });
                          }}
                          variant="ghost"
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 bg-transparent hover:bg-gray-100 rounded-none shadow-none ring-0 border-0 justify-start"
                          role="menuitem"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Export CSV
                          </div>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
            
            {canCreate && (
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="primary"
              >
                <svg className="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New Deal
              </Button>
            )}
          </div>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Filter Deals</h3>
                <Button 
                  onClick={clearFilters}
                  variant="link"
                  className="text-sm"
                >
                  Clear all filters
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Course Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course
                  </label>
                  <select
                    name="course_id"
                    value={filters.course_id || ''}
                    onChange={handleFilterChange}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Courses</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name} - {course.age_group} - Level {course.level} - {course.course_format}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Country Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    name="country"
                    value={filters.country}
                    onChange={handleFilterChange}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Countries</option>
                    <option value="Norway">Norway</option>
                    <option value="Sweden">Sweden</option>
                    <option value="DACH+">DACH+ (DE/AT/CH/LUX)</option>
                  </select>
                </div>
                
                {/* Deal Owner Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deal Owner
                  </label>
                  <select
                    name="deal_owner_id"
                    value={filters.deal_owner_id}
                    onChange={handleFilterChange}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Owners</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Create Date Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Creation Date
                  </label>
                  <div className="flex flex-col space-y-2">
                    <select
                      name="created_date_option"
                      value={filters.created_date_option || ""}
                      onChange={(e) => {
                        // Reset the specific created_after and created_before fields when option changes
                        if (e.target.value === "custom") {
                          setFilters({
                            ...filters,
                            created_date_option: e.target.value,
                            created_after: "",
                            created_before: ""
                          });
                        } else if (e.target.value === "") {
                          setFilters({
                            ...filters,
                            created_date_option: "",
                            created_after: "",
                            created_before: ""
                          });
                        } else {
                          // For predefined options, set the created_after value
                          const selectedOption = getDateRangeOptions().find(
                            (option, index) => index === parseInt(e.target.value)
                          );
                          if (selectedOption) {
                            setFilters({
                              ...filters,
                              created_date_option: e.target.value,
                              created_after: selectedOption.value,
                              created_before: ""
                            });
                          }
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Time</option>
                      {getDateRangeOptions().map((option, index) => (
                        <option key={index} value={index}>
                          {option.label}
                        </option>
                      ))}
                      <option value="custom">Custom Range</option>
                    </select>

                    {filters.created_date_option === "custom" && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            From
                          </label>
                          <input
                            type="date"
                            name="created_after"
                            value={filters.created_after ? filters.created_after.split('T')[0] : ""}
                            onChange={handleFilterChange}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            To
                          </label>
                          <input
                            type="date"
                            name="created_before"
                            value={filters.created_before ? filters.created_before.split('T')[0] : ""}
                            onChange={handleFilterChange}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Payment Date Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date
                  </label>
                  <div className="flex flex-col space-y-2">
                    <select
                      name="paid_date_option"
                      value={filters.paid_date_option || ""}
                      onChange={(e) => {
                        // Reset the specific paid_after and paid_before fields when option changes
                        if (e.target.value === "custom") {
                          setFilters({
                            ...filters,
                            paid_date_option: e.target.value,
                            paid_after: "",
                            paid_before: ""
                          });
                        } else if (e.target.value === "") {
                          setFilters({
                            ...filters,
                            paid_date_option: "",
                            paid_after: "",
                            paid_before: ""
                          });
                        } else {
                          // For predefined options, set the paid_after value
                          const selectedOption = getDateRangeOptions().find(
                            (option, index) => index === parseInt(e.target.value)
                          );
                          if (selectedOption) {
                            setFilters({
                              ...filters,
                              paid_date_option: e.target.value,
                              paid_after: selectedOption.value,
                              paid_before: ""
                            });
                          }
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Time</option>
                      {getDateRangeOptions().map((option, index) => (
                        <option key={index} value={index}>
                          {option.label}
                        </option>
                      ))}
                      <option value="custom">Custom Range</option>
                    </select>

                    {filters.paid_date_option === "custom" && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            From
                          </label>
                          <input
                            type="date"
                            name="paid_after"
                            value={filters.paid_after ? filters.paid_after.split('T')[0] : ""}
                            onChange={handleFilterChange}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            To
                          </label>
                          <input
                            type="date"
                            name="paid_before"
                            value={filters.paid_before ? filters.paid_before.split('T')[0] : ""}
                            onChange={handleFilterChange}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end space-x-2">
                <Button
                  onClick={clearFilters}
                  variant="outline"
                >
                  Clear Filters
                </Button>
                <Button
                  onClick={applyFilters}
                  variant="primary"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          )}

          {/* Content based on view mode */}
          {viewMode === 'list' ? (
            <div className="bg-white rounded-lg shadow">
              
              {/* Bulk Actions Bar - only shown when multiple deals are selected */}
              {selectedDeals.length > 1 && (
                <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedDeals.length} deals selected
                    </span>
                    <Button
                      onClick={() => {
                        setSelectedDeals([]);
                        setSelectAll(false);
                      }}
                      variant="link"
                      className="text-xs"
                    >
                      Clear selection
                    </Button>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        setBulkAction('update');
                        setBulkActionData({ 
                          stage_name: '', 
                          deal_owner_id: '', 
                          course_id: '',
                          country: '' 
                        });
                        setShowBulkActionModal(true);
                      }}
                      variant="primary"
                      size="md"
                    >
                      Bulk Update
                    </Button>
                    <Button
                      onClick={() => {
                        setBulkAction('delete');
                        setShowBulkActionModal(true);
                      }}
                      variant="danger"
                      size="md"
                    >
                      Bulk Delete
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Single Deal Selection Bar */}
              {selectedDeals.length === 1 && (
                <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-blue-700">
                      1 deal selected
                    </span>
                    <Button
                      onClick={() => {
                        setSelectedDeals([]);
                        setSelectAll(false);
                      }}
                      variant="link"
                      className="text-xs"
                    >
                      Clear selection
                    </Button>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleViewDeal(selectedDeals[0])}
                      variant="primary"
                      size="md"
                    >
                      View/Edit
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDeleteConfirm(true);
                      }}
                      variant="danger"
                      size="md"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
              
              {loading ? (
                <div className="text-center py-8">
                  <p>Loading deals...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600">
                  <p>{error}</p>
                </div>
              ) : deals.length === 0 ? (
                <div className="text-center py-8">
                  <p>No deals found. Create your first deal to get started.</p>
                </div>
              ) : (
                <DealsTableWithShared 
                  deals={deals}
                  selectedDeals={selectedDeals}
                  setSelectedDeals={setSelectedDeals}
                  selectAll={selectAll}
                  setSelectAll={setSelectAll}
                  handleSelectAll={handleSelectAll}
                  setSelectedDealId={setSelectedDealId}
                  setIsViewEditModalOpen={setIsViewEditModalOpen}
                  formatCurrency={formatCurrency}
                  updateDealStage={updateDealStage}
                  users={users}
                  setDeals={setDeals}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  handleSort={handleSort}
                  showColumnModal={showColumnModal}
                  setShowColumnModal={setShowColumnModal}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange} // Pass the handler
                  loading={loading}
                />
              )}
            </div>
          ) : (
            <PipelineView 
              filters={searchTerm ? {...activeFilters, search: searchTerm} : activeFilters} 
              onDealCreated={handleDealCreated}
            />
          )}
          
          {/* New Deal Modal */}
          <NewDealModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onDealCreated={handleDealCreated}
            prefillData={{}} // Add empty prefillData to match the pattern that works
          />
          
          {/* View/Edit Deal Modal */}
          <ViewEditDealModal
            isOpen={isViewEditModalOpen}
            onClose={() => setIsViewEditModalOpen(false)}
            onDealUpdated={handleDealUpdated}
            dealId={selectedDealId}
          />
          
          {/* CSV Import Modal */}
          <DealsImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            onImportComplete={(results) => {
              if (results.success) {
                showNotification(`Successfully imported ${results.importedCount} deals`, 'success');
                // Refresh the deals list after import
                fetchDeals();
              }
            }}
          />
          
          {/* Bulk Action Modal */}
          {showBulkActionModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 md:mx-0 p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Bulk Action</h3>
                  <p className="text-sm text-gray-600">
                    Apply action to {selectedDeals.length} selected deal(s):
                  </p>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="bulkAction" className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                      id="bulkAction"
                      value={bulkAction}
                      onChange={(e) => setBulkAction(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Select an action</option>
                      <option value="update">Update</option>
                      <option value="delete">Delete</option>
                    </select>
                  </div>
                  
                  {bulkAction === 'update' && (
                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      <div>
                        <label htmlFor="bulkStageName" className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                        <select
                          id="bulkStageName"
                          value={bulkActionData.stage_name}
                          onChange={(e) => setBulkActionData({...bulkActionData, stage_name: e.target.value})}
                          className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No change</option>
                          {bulkEditStages.map(stage => (
                            <option key={stage.id} value={stage.name}>{stage.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="bulkDealOwner" className="block text-sm font-medium text-gray-700 mb-1">Deal Owner</label>
                        <select
                          id="bulkDealOwner"
                          value={bulkActionData.deal_owner_id}
                          onChange={(e) => setBulkActionData({...bulkActionData, deal_owner_id: e.target.value})}
                          className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No change</option>
                          <option value="null">Unassign</option>
                          {users.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name} ({user.role})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="bulkCourse" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                        <select
                          id="bulkCourse"
                          value={bulkActionData.course_id || ''}
                          onChange={(e) => setBulkActionData({...bulkActionData, course_id: e.target.value})}
                          className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No change</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.name} - {course.age_group} - Level {course.level} - {course.course_format}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="bulkCountry" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <select
                          id="bulkCountry"
                          value={bulkActionData.country}
                          onChange={(e) => setBulkActionData({...bulkActionData, country: e.target.value})}
                          className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No change</option>
                          <option value="Norway">Norway</option>
                          <option value="Sweden">Sweden</option>
                          <option value="Germany">Germany</option>
                          <option value="Austria">Austria</option>
                          <option value="Switzerland">Switzerland</option>
                          <option value="Luxembourg">Luxembourg</option>
                        </select>
                      </div>
                    </div>
                  )}
                  
                  {bulkAction === 'delete' && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-700">
                        <p className="text-sm font-medium">Warning: This action cannot be undone.</p>
                        <p className="text-xs mt-1">All selected deals will be permanently deleted from the system.</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => {
                      setShowBulkActionModal(false);
                      setBulkAction('');
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    onClick={executeBulkAction}
                    variant={bulkAction === 'delete' ? 'danger' : 'primary'}
                    disabled={!bulkAction || (bulkAction === 'update' && !bulkActionData.stage_name && !bulkActionData.deal_owner_id && !bulkActionData.course_id && !bulkActionData.country)}
                  >
                    {bulkAction === 'delete' ? 'Delete Deals' : 'Update Deals'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : activeTab === 'invoices' ? (
        <InvoicesView />
      ) : activeTab === 'contracts' ? (
        <ContractsList />
      ) : null}
      
      {/* Refund Modal */}
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundDealId(null);
          setRefundStageId(null);
        }}
        onConfirm={handleRefundConfirm}
        dealValue={deals.find(d => d.id === refundDealId)?.value || 0}
        dealCurrency={deals.find(d => d.id === refundDealId)?.currency || 'USD'}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          executeBulkDelete();
          setShowDeleteConfirm(false);
        }}
        message={`Are you sure you want to delete ${selectedDeals.length} selected deal${selectedDeals.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        title="Delete Deals"
      />
      
    </div>
  );
};

export default DealsList;
